import types from './types';
import * as ViewHelper from 'utils/ViewHelper';
import { FILTER_TYPES } from '../../../utils/FilterHelper';

function reducer(state = {}, action = {}) {
  let availableColumns,
    currentView,
    defaultViewId,
    filters,
    index,
    newColumns,
    newState,
    screenViews,
    view,
    viewName,
    views;
  switch (action.type) {
    case types.INITIALIZE_VIEWS:
      // If views are already loaded nothing more to do
      screenViews = state[action.screenName];

      availableColumns = action.availableColumns;

      // Process views from service response into UI data model format and add system default view
      views = {};
      if (action.views) {
        action.views.forEach((view) => {
          views[view.name] = updateLoadedView(view, availableColumns);
        });
      }
      views.default = createDefaultView(action.defaultViewConfig, availableColumns);

      // Set current view to either user selected default or system default
      defaultViewId = action.defaultViewId;

      if (action.viewName === null || !views?.[action.viewName]) {
        currentView = cloneView(getDefaultView(defaultViewId, views));
      } else {
        currentView = cloneView(views[action.viewName]);
      }

      return Object.assign({}, state, {
        [action.screenName]: {
          columnOverrides: action.columnOverrides,
          availableColumns: availableColumns,
          view: views,
          currentView: currentView,
          viewsLoaded: true,
          defaultViewId: defaultViewId,
        },
      });

    case types.RESET_DEFAULT_VIEW:
      // Views must already be loaded
      screenViews = state[action.screenName];
      if (!screenViews || !screenViews.viewsLoaded) {
        return state;
      }

      views = state[action.screenName].view;
      availableColumns = state[action.screenName].availableColumns;
      currentView = state[action.screenName].currentView;
      const defaultView = createDefaultView(action.defaultViewConfig, availableColumns);

      if (currentView.name === 'default') {
        currentView = cloneView(defaultView);
      }

      return Object.assign({}, state, {
        [action.screenName]: Object.assign({}, state[action.screenName], {
          view: Object.assign({}, views, {
            default: defaultView,
          }),
          currentView: currentView,
        }),
      });

    case types.SWITCH_VIEW:
      if (!state[action.screenName]) {
        return state;
      }
      if (action.viewName === null) {
        view = getDefaultView(state[action.screenName].defaultViewId, state[action.screenName].view);
      } else {
        view = state[action.screenName].view[action.viewName];
      }
      if (!view) {
        throw `Attempt to switch view to ${
          action.viewName === null ? '(default view)' : action.viewName
        } but this view does not exist`;
      }

      return Object.assign({}, state, {
        [action.screenName]: {
          ...state[action.screenName],
          currentView: cloneView(view),
        },
      });

    case types.UPDATE_FILTERS:
      newState = cloneTable(state, action.screenName);
      currentView = newState[action.screenName].currentView;

      // Put all filters into array in order of position
      filters = sortFilters(currentView);

      // Remove filters from sorted list of filters
      let removeIndex;
      if (action.removeFilters) {
        action.removeFilters.forEach((filter) => {
          removeIndex = filters.findIndex((filterItem) => {
            return filterItem.fieldName === filter.fieldName;
          });
          filters.splice(removeIndex, 1);
        });
      }

      // Add filters to sorted list of filters
      if (action.addFilters) {
        action.addFilters.forEach((filter) => {
          index = findFilterIndex(currentView[filter.type], filter.fieldName);
          if (index !== -1) {
            throw `Attempt to add filter for field (${filter.fieldName}) but a filter is already defined for this field`;
          } else {
            filters.push(filter);
          }
        });
      }

      if (action.changeFilters) {
        action.changeFilters.forEach((filter) => {
          index = filters.findIndex((existingFilter) => existingFilter.fieldName === filter.fieldName);
          if (!(index >= 0)) {
            throw `Attempt to change filter for field (${filter.fieldName}) but no filter is defined for this field.`;
          }
          //Only filterStyle is currently updatable in this manner.
          filters[index].filterStyle = filter.filterStyle;
        });
      }

      // Update table filter details from sorted list of filters
      filters = makeFiltersContiguous(filters);
      updateSortedFilters(filters, currentView);
      return newState;

    case types.UPDATE_FILTER:
      newState = cloneTable(state, action.screenName);
      currentView = newState[action.screenName].currentView;
      updateFilter(currentView, action.filter);
      return newState;

    case types.UPDATE_GENERIC_FILTER:
      newState = cloneTable(state, action.screenName);
      currentView = newState[action.screenName].currentView;
      currentView.genericFilter = action.filterText;
      return newState;

    case types.UPDATE_COLUMNS:
      newState = cloneTable(state, action.screenName);
      currentView = newState[action.screenName].currentView;
      currentView.columns = action.selectedColumns;
      return newState;

    case types.MOVE_COLUMN:
      newState = cloneTable(state, action.screenName);
      currentView = newState[action.screenName].currentView;
      newColumns = ViewHelper.moveColumn(currentView.columns, action.fieldName, action.newIndex);
      currentView.columns = newColumns;
      return newState;

    case types.RESIZE_COLUMN:
      newState = cloneTable(state, action.screenName);
      currentView = newState[action.screenName].currentView;
      newColumns = currentView.columns.slice();
      index = findColumnIndex(newColumns, action.fieldName);
      newColumns[index] = Object.assign({}, newColumns[index], {
        width: action.newWidth,
      });
      currentView.columns = newColumns;
      return newState;

    case types.UPDATE_SORT:
      newState = cloneTable(state, action.screenName);
      currentView = newState[action.screenName].currentView;
      currentView.orderBy = [{ fieldName: action.fieldName, isDescending: action.isDescending }];
      return newState;

    case types.SAVE_VIEW:
      // Add/update the view in the loaded details
      // Also update the view as the current View (to load id details etc)
      view = action.response;
      updateLoadedView(view, state[action.screenName].availableColumns);

      return Object.assign({}, state, {
        [action.screenName]: {
          ...state[action.screenName],
          currentView: cloneView(view),
          view: {
            ...state[action.screenName].view,
            [action.viewName]: view,
          },
        },
      });

    case types.TOGGLE_HIDE_CONFIGURATION:
      return Object.assign({}, state, {
        [action.screenName]: {
          ...state[action.screenName],
          currentView: {
            ...state[action.screenName].currentView,
            hideConfiguration: !state[action.screenName].currentView.hideConfiguration,
          },
        },
      });

    case types.DELETE_VIEW_SUCCESS:
      // Remove deleted view from set of defined views
      views = Object.assign({}, state[action.screenName].view);
      viewName = Object.keys(views).find((viewName) => {
        return views[viewName].id === action.viewId;
      });
      delete views[viewName];

      // If view deleted was the default view reset default view to view named "default" which has no id
      defaultViewId = state[action.screenName].defaultViewId;
      if (action.viewId === defaultViewId) {
        defaultViewId = null;
      }

      // If view deleted was the current view reset current view to the default view
      currentView = state[action.screenName].currentView;
      if (action.viewId === currentView.id) {
        currentView = cloneView(getDefaultView(defaultViewId, views));
      }

      return Object.assign({}, state, {
        [action.screenName]: {
          ...state[action.screenName],
          defaultViewId: defaultViewId,
          view: views,
          currentView: currentView,
        },
      });

    case types.UPDATE_VIEW_CONCURRENCY:
      views = Object.assign({}, state[action.screenName].view);
      viewName = Object.keys(views).find((viewName) => {
        return views[viewName].id === action.viewId;
      });
      views[viewName].concurrencyVersion++;
      return Object.assign({}, state, {
        [action.screenName]: {
          ...state[action.screenName],
          view: views,
        },
      });

    case types.SET_DEFAULT_VIEW_SUCCESS:
      return Object.assign({}, state, {
        [action.MetaData.screenName]: {
          ...state[action.MetaData.screenName],
          defaultViewId: action.MetaData.defaultViewId,
        },
      });

    default:
      return state;
  }
}

// Clone requested view
function cloneView(view) {
  const newView = Object.assign({}, view);

  newView.columns = (newView.columns ?? []).map((column) => {
    return Object.assign({}, column);
  });
  newView.orderBy = newView.orderBy.map((orderBy) => {
    return Object.assign({}, orderBy);
  });
  FILTER_TYPES.forEach((filterType) => {
    if (newView[filterType]) {
      newView[filterType] = newView[filterType].map((filterItem) => {
        return Object.assign({}, filterItem);
      });
    }
  });

  return newView;
}

// Clone state creating new objects in property chain to table
function cloneTable(state, screenName) {
  const newState = Object.assign({}, state, {
    [screenName]: {
      ...state[screenName],
      currentView: { ...state[screenName]?.currentView },
    },
  });
  const view = newState[screenName].currentView;

  if (view) {
    view.columns = (view.columns ?? []).map((column) => {
      return Object.assign({}, column);
    });
    view.orderBy = (view.orderBy ?? []).map((orderBy) => {
      return Object.assign({}, orderBy);
    });
    FILTER_TYPES.forEach((filterType) => {
      if (view[filterType]) {
        view[filterType] = view[filterType].map((filterItem) => {
          return Object.assign({}, filterItem);
        });
      }
    });
  }
  return newState;
}

function findFilterIndex(filters, fieldName) {
  if (!filters) {
    return -1;
  }
  return filters.findIndex((filter) => {
    return filter.fieldName === fieldName;
  });
}

function findColumnIndex(columns, fieldName) {
  if (!columns) {
    return -1;
  }
  return columns.findIndex((column) => {
    return column.fieldName === fieldName;
  });
}

function updateLoadedView(view, availableColumns) {
  const fieldNameToColumnMap = ViewHelper.getFieldNameToColumnMap(availableColumns);

  // Remove any columns that are not valid and order based on specified positions
  let columnDefinition;
  const columns = [];
  view.columns.forEach((column) => {
    columnDefinition = fieldNameToColumnMap[column.fieldName];
    if (columnDefinition) {
      columns[column.position] = Object.assign({}, column, {
        title: columnDefinition.title,
        formatter: columnDefinition.formatter,
        totalCalculation: columnDefinition.totalCalculation,
      });
    } else {
      console.warn(`No column definition found for ${column.fieldName}`);
    }
  });

  // Re-order columns to be contiguous and update to table
  view.columns = [];
  let pos = 0;
  columns.forEach((column) => {
    column.position = pos++;
    view.columns.push(column);
  });

  // Remove any filters that are not valid and ensure positions are contiguous
  const filters = makeFiltersContiguous(sortFilters(view, fieldNameToColumnMap));
  updateSortedFilters(filters, view);

  view.filterAttributeObjects?.forEach((value) => {
    if (value?.codes) {
      value.objects = value.codes.map((value) => ({ id: value }));
    }
  });

  return view;
}

// Create default view from defaultViewConfig
function createDefaultView(defaultViewConfig, availableColumns) {
  let columns, fieldNameToColumnMap;
  if (!defaultViewConfig) {
    return null;
  }

  if (defaultViewConfig) {
    const { name, orderBy, visibleFieldNames, screen, genericFilter } = defaultViewConfig;
    if (!availableColumns) {
      return;
    }
    fieldNameToColumnMap = ViewHelper.getFieldNameToColumnMap(availableColumns);
    columns = [];

    visibleFieldNames.forEach((fieldName, index) => {
      if (fieldNameToColumnMap[fieldName]) {
        columns.push(
          Object.assign({}, fieldNameToColumnMap[fieldName], {
            position: index,
            total: defaultViewConfig.defaultTotalColumns?.find((totalColumnName) => totalColumnName === fieldName),
          })
        );
      }
    });

    return { name: name, orderBy: orderBy, columns: columns, screen: screen, genericFilter: genericFilter };
  }
}

// Put all filters into sorted array in order of position
// If availableColumns is provided then any filters where field name is not a property of availableColumns are excluded
function sortFilters(view, fieldNameToColumnMap) {
  return FILTER_TYPES.flatMap((filterType) => {
    if (view[filterType]) {
      return view[filterType]
        .filter((filter) => !fieldNameToColumnMap || fieldNameToColumnMap[filter.fieldName])
        .map((filter) => {
          return Object.assign({}, filter, {
            type: filterType,
          });
        });
    }
    return [];
  }).sort((a, b) => a.position - b.position);
}

// Make filter positions contiguous
function makeFiltersContiguous(filters) {
  const contiguousFilters = [];
  filters.forEach((filter) => {
    filter.position = contiguousFilters.length;
    contiguousFilters.push(filter);
  });

  return contiguousFilters;
}

// Update table filter details from sorted list of filters
function updateSortedFilters(filters, view) {
  FILTER_TYPES.forEach((filterType) => {
    delete view[filterType];
  });
  filters.forEach((filter, index) => {
    if (!view[filter.type]) {
      view[filter.type] = [];
    }
    view[filter.type].push(Object.assign({}, filter, { position: index }));
  });
}

// Updates the given newFilter over an existing filter within a table
// The table object is updated directly so must be passed an object that is suitable for direct assignment
function updateFilter(table, newFilter) {
  let filters, index;

  filters = table[newFilter.type] ? [...table[newFilter.type]] : [];
  table[newFilter.type] = filters;
  index = filters.findIndex((filter) => {
    return filter.fieldName === newFilter.fieldName;
  });
  if (index !== -1) {
    filters[index] = newFilter;
  }
}

// Gets the default view given a view id
function getDefaultView(defaultViewId, views) {
  if (defaultViewId !== null) {
    const defaultViewName = Object.keys(views).find((viewName) => {
      return views[viewName].id === defaultViewId;
    });
    if (defaultViewName !== null) {
      return views[defaultViewName];
    }
  }

  return views.default;
}

function getFormatter(classType) {
  switch (classType) {
    case 'LocalDate':
      return 'date';

    case 'Long':
      return 'numeric';
  }
  return null;
}

export default reducer;
